
import { useState, useEffect, useCallback } from 'react';

const navLinks = [
  { label: 'Join Waitlist', href: '#waitlist' },
  { label: 'Features', href: '#features' },
  { label: 'How It Works', href: '#how-it-works' },
  { label: 'Benefits', href: '#benefits' },
];

function scrollToSection(e: React.MouseEvent<HTMLAnchorElement>, href: string) {
  e.preventDefault();
  if (href === '#waitlist') {
    const formCard = document.getElementById('waitlist-form-card');
    const nameInput = document.getElementById('name');
    if (formCard) {
      const isMobile = window.innerWidth < 768;
      formCard.scrollIntoView({
        behavior: 'smooth',
        block: isMobile ? 'start' : 'center',
      });
      setTimeout(() => {
        if (nameInput) {
          (nameInput as HTMLInputElement).focus();
        }
      }, isMobile ? 1000 : 800);
    }
  } else {
    const id = href.replace('#', '');
    const el = document.getElementById(id);
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }
}

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    let ticking = false;
    const handleScroll = () => {
      if (!ticking) {
        window.requestAnimationFrame(() => {
          setScrolled(window.scrollY > 50);
          ticking = false;
        });
        ticking = true;
      }
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleLinkClick = useCallback(
    (e: React.MouseEvent<HTMLAnchorElement>, href: string) => {
      scrollToSection(e, href);
      setMobileOpen(false);
    },
    [],
  );

  // Lock body scroll when mobile menu is open
  useEffect(() => {
    if (mobileOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [mobileOpen]);

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 will-change-[background-color,box-shadow] ${
        scrolled || mobileOpen ? 'bg-white shadow-md' : 'bg-transparent'
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-4 flex items-center justify-between">
        {/* Logo */}
        <a href="#" onClick={(e) => { e.preventDefault(); window.scrollTo({ top: 0, behavior: 'smooth' }); }} className="flex items-center gap-3 cursor-pointer">
          <img
            src="https://static.readdy.ai/image/9d259c98c343951903198f09594f7048/db8b95289cbac60a531f359fde0857bb.png"
            alt="HappyFin Logo"
            className="h-10 w-10"
            width={40}
            height={40}
          />
          <h1 className="text-2xl font-bold text-gray-900">HappyFin</h1>
        </a>

        {/* Desktop links */}
        <div className="hidden md:flex items-center gap-8">
          {navLinks.map((link) =>
            link.href === '#waitlist' ? (
              <a
                key={link.href}
                href={link.href}
                onClick={(e) => handleLinkClick(e, link.href)}
                className="inline-flex items-center gap-1.5 px-5 py-2 bg-lime-500 text-white text-sm font-semibold rounded-full hover:bg-lime-600 transition-all hover:scale-105 shadow-sm whitespace-nowrap cursor-pointer"
              >
                {link.label}
                <i className="ri-arrow-right-line text-sm"></i>
              </a>
            ) : (
              <a
                key={link.href}
                href={link.href}
                onClick={(e) => handleLinkClick(e, link.href)}
                className="text-sm font-medium text-gray-700 hover:text-lime-600 transition-colors cursor-pointer"
              >
                {link.label}
              </a>
            ),
          )}
        </div>

        {/* Mobile hamburger */}
        <button
          onClick={() => setMobileOpen((prev) => !prev)}
          className="md:hidden w-10 h-10 flex items-center justify-center rounded-lg hover:bg-gray-100 transition-colors cursor-pointer"
          aria-label="Toggle menu"
        >
          <i className={`${mobileOpen ? 'ri-close-line' : 'ri-menu-3-line'} text-2xl text-gray-900`}></i>
        </button>
      </div>

      {/* Mobile menu */}
      <div
        className={`md:hidden overflow-hidden transition-all duration-300 ease-in-out ${
          mobileOpen ? 'max-h-80 opacity-100' : 'max-h-0 opacity-0'
        }`}
      >
        <div className="px-4 pb-6 pt-2 flex flex-col gap-1 bg-white">
          {navLinks.map((link) => (
            <a
              key={link.href}
              href={link.href}
              onClick={(e) => handleLinkClick(e, link.href)}
              className={`block px-4 py-3 rounded-lg text-sm font-medium transition-colors cursor-pointer ${
                link.href === '#waitlist'
                  ? 'bg-lime-500 text-white text-center mt-2 hover:bg-lime-600'
                  : 'text-gray-700 hover:bg-gray-50 hover:text-lime-600'
              }`}
            >
              {link.label}
            </a>
          ))}
        </div>
      </div>
    </nav>
  );
}
